// Blocking modal shown when an admin has issued this user a temporary
// password (user.must_change_password). The user must set their own
// password before continuing; the backend clears the flag on success.
import React, { useState } from 'react';
import axios from 'axios';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { PasswordStrengthMeter } from './PasswordStrengthMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ForcePasswordChangeModal = ({ token, onLogout }) => {
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(
        `${API}/domain/change-password`,
        { current_password: currentPw, new_password: newPw },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success('Password updated — signing you back in');
      setTimeout(() => window.location.reload(), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="force-password-change-modal">
      <form onSubmit={submit} className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-teal-50 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-teal-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-slate-900">Set a new password</h2>
            <p className="text-xs text-slate-500">An admin issued you a temporary password. Choose your own to continue.</p>
          </div>
        </div>
        <div className="space-y-3">
          <input
            type="password"
            required
            value={currentPw}
            onChange={(e) => setCurrentPw(e.target.value)}
            placeholder="Temporary password (the one you just used)"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="force-change-current-password"
          />
          <input
            type="password"
            required
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            placeholder="New password (min 8 characters)"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="force-change-new-password"
          />
          <PasswordStrengthMeter password={newPw} />
          <input
            type="password"
            required
            value={confirmPw}
            onChange={(e) => setConfirmPw(e.target.value)}
            placeholder="Confirm new password"
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
            data-testid="force-change-confirm-password"
          />
        </div>
        <div className="flex items-center justify-between pt-1">
          <button
            type="button"
            onClick={onLogout}
            className="text-xs text-slate-400 hover:text-slate-600"
            data-testid="force-change-logout"
          >
            Log out instead
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 bg-teal-600 hover:bg-teal-500 text-white text-sm font-semibold px-4 py-2 rounded-lg disabled:opacity-60"
            data-testid="force-change-submit"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save new password
          </button>
        </div>
      </form>
    </div>
  );
};
